// Hobbli Pinball: Orbital Vault
// Geometry-checked 2D pinball physics + canvas renderer (V5).
// No third-party physics or art dependency is required.

export const TABLE_WIDTH = 720;
export const TABLE_HEIGHT = 1080;

const BALL_RADIUS = 14;
const GRAVITY = 650;
const MAX_SPEED = 1650;
const WALL_RESTITUTION = 0.71;
const FLIPPER_RADIUS = 13;
const FIXED_STEP = 1 / 240;

const LEFT_FLIPPER = {
  pivot: { x: 244, y: 930 },
  length: 80,
  rest: 0.22,
  active: -0.72,
};

const RIGHT_FLIPPER = {
  pivot: { x: 476, y: 930 },
  length: 80,
  rest: Math.PI - 0.22,
  active: Math.PI + 0.72,
};

// V5: physical paths and ball clearance take priority over decorative art.
// The geometry is audited against posts, targets, bumpers and flipper sweeps.
// Keep this list, sensor pathways, and rendering in sync.
const STATIC_SEGMENTS = [
  // Main cabinet / upper arch.
  { a: [48, 846], b: [48, 165], kind: "wall" },
  { a: [48, 165], b: [77, 105], kind: "wall" },
  { a: [77, 105], b: [145, 58], kind: "wall" },
  { a: [145, 58], b: [544, 58], kind: "wall" },
  { a: [544, 58], b: [590, 92], kind: "wall" },

  // Shooter lane. V4 deliberately leaves the TOP OF THE LANE OPEN.
  // V3 had two diagonal metal segments hanging over the lane; a hard launch
  // could collide with their underside and become trapped. The right wall and
  // divider are now simple, parallel rails and a one-way rollover transition
  // below handles the launch into the playfield.
  { a: [683, 76], b: [683, 1016], kind: "wall" },
  // Continuous divider prevents live balls from falling into the waiting
  // launch cradle through an opening underneath the lower right apron.
  { a: [621, 214], b: [621, 1012], kind: "wall" },

  // Bottom cabinet rails and apron. The center is intentionally open.
  { a: [48, 846], b: [78, 887], kind: "wall" },
  { a: [78, 887], b: [176, 980], kind: "wall" },
  { a: [176, 980], b: [258, 1021], kind: "wall" },
  { a: [621, 842], b: [570, 887], kind: "wall" },
  { a: [570, 887], b: [544, 955], kind: "wall" },
  { a: [544, 955], b: [462, 1021], kind: "wall" },

  // V6 inlanes: continuous physical guides that angle toward the flipper
  // roots, leaving a ball-wide exit and a separate accessible side outlane.
  // Avoid closing the opening to the flippers with an end post or a V-shaped
  // pocket.  Swept-flipper/guide gap is checked in geometry_audit.mjs.
  { a: [153, 690], b: [151, 759], kind: "inlane" },
  { a: [151, 759], b: [168, 819], kind: "inlane" },
  { a: [168, 819], b: [194, 859], kind: "inlane" },
  { a: [194, 859], b: [213, 886], kind: "inlane" },
  { a: [567, 690], b: [569, 759], kind: "inlane" },
  { a: [569, 759], b: [552, 819], kind: "inlane" },
  { a: [552, 819], b: [526, 859], kind: "inlane" },
  { a: [526, 859], b: [507, 886], kind: "inlane" },

  // Classic upper orbit guides.
  { a: [114, 176], b: [97, 272], kind: "wall" },
  { a: [97, 272], b: [119, 354], kind: "wall" },
  // The old guide left <one ball diameter> against the shooter divider.
  { a: [546, 176], b: [550, 272], kind: "wall" },
  { a: [550, 272], b: [542, 354], kind: "wall" },

  // Center ramp rails.
  { a: [302, 430], b: [316, 347], kind: "wall" },
  { a: [316, 347], b: [347, 316], kind: "wall" },
  { a: [418, 430], b: [404, 347], kind: "wall" },
  { a: [404, 347], b: [373, 316], kind: "wall" },

  // Shooter-lane cradle.
  { a: [620, 1012], b: [683, 1012], kind: "wall" },
];

const SLINGSHOTS = [
  { id: "left", a: [201, 772], b: [244, 842], boost: 350 },
  { id: "right", a: [519, 772], b: [476, 842], boost: 350 },
];

const BUMPERS = [
  { id: 0, x: 266, y: 278, r: 38, boost: 560 },
  { id: 1, x: 360, y: 221, r: 39, boost: 585 },
  { id: 2, x: 454, y: 278, r: 38, boost: 560 },
];

const POSTS = [
  { x: 225, y: 657, r: 11 },
  { x: 495, y: 657, r: 11 },
  // Visible, active rescue post above the center drain. It is deliberately
  // above the rest-position flipper tips with ball-wide approach clearance.
  { id: "center-save", x: 360, y: 995, r: 11 },
  // Lower apron posts would constrict return lanes and one overlapped
  // the right outlane rail; flipper pivots already provide the hardware.
  // No posts within the swept areas of either flipper.
  // Flipper safety arcs are tested below at import time.
  // Former (117,592) post was INSIDE mission target 3; (603,592)
  // blocked the entire WARP/shooter-divider corridor. Both removed.
];

// Kickbacks should be an earned, visible game feature if added in future;
// no hidden rescue zone should change a ball's course during normal play.

const MISSION_TARGETS = [
  { id: 0, x: 110, y: 404, w: 23, h: 58 },
  { id: 1, x: 110, y: 477, w: 23, h: 58 },
  { id: 2, x: 110, y: 550, w: 23, h: 58 },
];

const WARP_TARGETS = [
  { id: 0, x: 511, y: 402, w: 23, h: 58 },
  { id: 1, x: 511, y: 475, w: 23, h: 58 },
  { id: 2, x: 511, y: 548, w: 23, h: 58 },
];

const SENSORS = [
  { id: "lane-0", type: "lane", index: 0, x: 154, y: 105, w: 52, h: 76 },
  { id: "lane-1", type: "lane", index: 1, x: 221, y: 94, w: 52, h: 76 },
  { id: "lane-2", type: "lane", index: 2, x: 288, y: 88, w: 52, h: 76 },
  { id: "lane-3", type: "lane", index: 3, x: 355, y: 88, w: 52, h: 76 },
  { id: "lane-4", type: "lane", index: 4, x: 422, y: 94, w: 52, h: 76 },
  { id: "lane-5", type: "lane", index: 5, x: 489, y: 105, w: 52, h: 76 },

  // Score the actual entrance shot, not any ball sitting in a huge region.
  { id: "left-orbit", type: "orbit-left", x: 67, y: 236, w: 42, h: 64, direction: "up", minSpeed: 120 },
  { id: "right-orbit", type: "orbit-right", x: 563, y: 236, w: 40, h: 64, direction: "up", minSpeed: 120 },
  { id: "center-ramp", type: "ramp", x: 335, y: 340, w: 50, h: 60, direction: "up", minSpeed: 120 },
];

const REACTOR = { x: 360, y: 565, r: 55 };

// Continuous guided wireform at the top of the shooter lane. No teleport, and
// it cannot be entered from a rolling, soft launch. The guide is drawn below.
const SHOOTER_PATH = {
  start: [649, 212],
  c1: [657, 104],
  c2: [568, 96],
  end: [511, 168],
  duration: 0.56,
};

// Elevated wireform/orbit returns. Entry requires an aimed UPWARD shot through
// a genuine narrow entrance. The ball follows a drawn track continuously,
// then re-enters normal physics with an intentionally chosen return vector.
// These are not colliders: a wireform carries the ball above the playfield.
const SHOT_PATHS = {
  "orbit-left": {
    c1: [70, 135], c2: [170, 106], end: [241, 173],
    duration: 1.02, exitVelocity: [260, 230],
  },
  "orbit-right": {
    c1: [591, 137], c2: [510, 109], end: [453, 174],
    duration: 1.02, exitVelocity: [-265, 230],
  },
  ramp: {
    c1: [324, 226], c2: [492, 237], end: [475, 442],
    duration: 0.94, exitVelocity: [-110, 330],
  },
};

function makeRoute(ball, definition, type) {
  return {
    ...definition,
    start: [ball.x, ball.y],
    t: 0,
    type,
    entrySpeed: Math.hypot(ball.vx, ball.vy),
  };
}

function cubicPoint(path, t) {
  const u = 1 - t;
  return {
    x: u*u*u*path.start[0] + 3*u*u*t*path.c1[0] + 3*u*t*t*path.c2[0] + t*t*t*path.end[0],
    y: u*u*u*path.start[1] + 3*u*u*t*path.c1[1] + 3*u*t*t*path.c2[1] + t*t*t*path.end[1],
  };
}

const seededStars = (() => {
  let seed = 912731;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: 150 }, () => ({
    x: 50 + rand() * 620,
    y: 48 + rand() * 930,
    r: 0.5 + rand() * 1.8,
    a: 0.2 + rand() * 0.7,
  }));
})();

let nextBallId = 1;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function length(x, y) {
  return Math.sqrt(x * x + y * y);
}

function closestPointOnSegment(px, py, ax, ay, bx, by) {
  const abx = bx - ax;
  const aby = by - ay;
  const ab2 = abx * abx + aby * aby || 1;
  const t = clamp(((px - ax) * abx + (py - ay) * aby) / ab2, 0, 1);
  return { x: ax + abx * t, y: ay + aby * t, t };
}

function normalize(x, y) {
  const len = length(x, y) || 1;
  return { x: x / len, y: y / len };
}

function limitBallSpeed(ball) {
  const speed = length(ball.vx, ball.vy);
  if (speed > MAX_SPEED) {
    const scale = MAX_SPEED / speed;
    ball.vx *= scale;
    ball.vy *= scale;
  }
}

function spawnEffect(world, x, y, color = "#7efcff", count = 8, force = 90) {
  for (let i = 0; i < count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = force * (0.45 + Math.random() * 0.75);
    world.effects.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0.36 + Math.random() * 0.3,
      maxLife: 0.66,
      size: 1.5 + Math.random() * 3.5,
      color,
    });
  }
  if (world.effects.length > 180) {
    world.effects.splice(0, world.effects.length - 180);
  }
}

function makeBall(x = 649, y = 982, vx = 0, vy = 0) {
  return {
    id: nextBallId++,
    x,
    y,
    vx,
    vy,
    r: BALL_RADIUS,
    trail: [],
    sensorCooldowns: {},
    collisionCooldowns: {},
    shooterReady: x > 620 && y > 900,
    pendingSoftReturn: false,
    lastX: x,
    lastY: y,
    stuckFor: 0,
    kickbacksUsed: {},
    route: null,
    slingChain: 0,
    lastSlingTime: -10,
  };
}

export function createPinballWorld() {
  return {
    time: 0,
    accumulator: 0,
    balls: [],
    effects: [],
    leftFlipper: { angle: LEFT_FLIPPER.rest, omega: 0 },
    rightFlipper: { angle: RIGHT_FLIPPER.rest, omega: 0 },
    plungerPower: 0,
    plungerHeld: false,
    shake: 0,
    flash: 0,
  };
}

export function resetPinballWorld(world) {
  world.time = 0;
  world.accumulator = 0;
  world.balls = [];
  world.effects = [];
  world.leftFlipper = { angle: LEFT_FLIPPER.rest, omega: 0 };
  world.rightFlipper = { angle: RIGHT_FLIPPER.rest, omega: 0 };
  world.plungerPower = 0;
  world.plungerHeld = false;
  world.shake = 0;
  world.flash = 0;
}

export function servePinball(world) {
  world.balls.push(makeBall());
  world.plungerPower = 0;
  world.plungerHeld = false;
}

export function addMultiball(world, count = 2) {
  // Balls must start outside the reactor body AND each other. Old spawns
  // overlapped the central collider and were forcibly ejected at huge speed.
  for (let i = 0; i < count; i += 1) {
    const side = i % 2 === 0 ? -1 : 1;
    const row = Math.floor(i / 2);
    world.balls.push(makeBall(
      REACTOR.x + side * (108 + row * 26),
      REACTOR.y - 65 - row * 46,
      side * (190 + row * 28),
      -(620 + row * 50)
    ));
  }
  world.flash = 1;
  world.shake = Math.max(world.shake, 7);
  spawnEffect(world, REACTOR.x, REACTOR.y, "#ff53e8", 30, 180);
}

function collideBallSegment(ball, segment, restitution = WALL_RESTITUTION) {
  const [ax, ay] = segment.a;
  const [bx, by] = segment.b;
  const point = closestPointOnSegment(ball.x, ball.y, ax, ay, bx, by);
  let dx = ball.x - point.x;
  let dy = ball.y - point.y;
  let dist = length(dx, dy);
  const minDist = ball.r + (segment.radius || 4);

  if (dist >= minDist) return false;

  if (dist < 0.0001) {
    const sx = bx - ax;
    const sy = by - ay;
    const n = normalize(-sy, sx);
    dx = n.x;
    dy = n.y;
    dist = 1;
  }

  const nx = dx / dist;
  const ny = dy / dist;
  const penetration = minDist - dist;
  ball.x += nx * penetration;
  ball.y += ny * penetration;

  const vn = ball.vx * nx + ball.vy * ny;
  if (vn < 0) {
    ball.vx -= (1 + restitution) * vn * nx;
    ball.vy -= (1 + restitution) * vn * ny;
  }
  limitBallSpeed(ball);
  return true;
}

// Collision functions provide *only* contact resolution. Powered elements
// award an impulse ONCE, after an approaching impact passes its cooldown.
// This avoids the runaway speed from continuously boosting persistent contacts.
function collideBallCircle(ball, circle, restitution = 0.82) {
  let dx = ball.x - circle.x;
  let dy = ball.y - circle.y;
  let distance = length(dx, dy);
  const minimum = ball.r + circle.r;
  if (distance >= minimum) return false;
  if (distance < 1e-6) {
    const outward = normalize(ball.vx || 0.3, ball.vy || -1);
    dx = outward.x;
    dy = outward.y;
    distance = 1;
  }
  const nx = dx / distance;
  const ny = dy / distance;
  ball.x += nx * (minimum - distance + 0.01);
  ball.y += ny * (minimum - distance + 0.01);
  const vn = ball.vx * nx + ball.vy * ny;
  if (vn < 0) {
    ball.vx -= (1 + restitution) * vn * nx;
    ball.vy -= (1 + restitution) * vn * ny;
  }
  return { nx, ny, incomingSpeed: Math.max(0, -vn) };
}

function collideBallRect(ball, rect, restitution = 0.72) {
  const closestX = clamp(ball.x, rect.x, rect.x + rect.w);
  const closestY = clamp(ball.y, rect.y, rect.y + rect.h);
  let dx = ball.x - closestX;
  let dy = ball.y - closestY;
  let distance = length(dx, dy);
  if (distance >= ball.r) return false;
  let nx, ny;
  if (distance < 0.000001) {
    // An embedded ball must be moved all the way outside, not by 13 px.
    const faces = [
      { d: ball.x - rect.x, nx: -1, ny: 0, x: rect.x - ball.r, y: ball.y },
      { d: rect.x + rect.w - ball.x, nx: 1, ny: 0, x: rect.x + rect.w + ball.r, y: ball.y },
      { d: ball.y - rect.y, nx: 0, ny: -1, x: ball.x, y: rect.y - ball.r },
      { d: rect.y + rect.h - ball.y, nx: 0, ny: 1, x: ball.x, y: rect.y + rect.h + ball.r },
    ];
    const face = faces.reduce((best, item) => item.d < best.d ? item : best);
    nx = face.nx;
    ny = face.ny;
    ball.x = face.x;
    ball.y = face.y;
  } else {
    nx = dx / distance;
    ny = dy / distance;
    ball.x += nx * (ball.r - distance + 0.01);
    ball.y += ny * (ball.r - distance + 0.01);
  }
  const vn = ball.vx * nx + ball.vy * ny;
  if (vn < 0) {
    ball.vx -= (1 + restitution) * vn * nx;
    ball.vy -= (1 + restitution) * vn * ny;
  }
  return { nx, ny, incomingSpeed: Math.max(0, -vn) };
}

function poweredImpact(ball, hit, impulse) {
  if (!hit || hit.incomingSpeed < 25) return;
  // Set a minimum outbound normal speed rather than piling on every impulse.
  const outbound = ball.vx * hit.nx + ball.vy * hit.ny;
  const adjustment = Math.max(0, Math.min(impulse, impulse - outbound));
  ball.vx += hit.nx * adjustment;
  ball.vy += hit.ny * adjustment;
  limitBallSpeed(ball);
}

function flipperTip(definition, angle) {
  return {
    x: definition.pivot.x + Math.cos(angle) * definition.length,
    y: definition.pivot.y + Math.sin(angle) * definition.length,
  };
}

function updateFlipper(flipper, definition, pressed, dt) {
  // Constant angular speed models a real flipper's quick stroke and slower
  // return. Exponential easing previously generated absurd angular speeds
  // (10,000+ px/s at the tip) on the first step.
  const target = pressed ? definition.active : definition.rest;
  const previous = flipper.angle;
  const speed = pressed ? 15.2 : 10.5; // radians per second
  flipper.angle += clamp(target - previous, -speed * dt, speed * dt);
  flipper.omega = (flipper.angle - previous) / dt;
}

function collideBallFlipper(ball, flipper, definition) {
  const tip = flipperTip(definition, flipper.angle);
  const point = closestPointOnSegment(
    ball.x, ball.y, definition.pivot.x, definition.pivot.y, tip.x, tip.y
  );
  let dx = ball.x - point.x;
  let dy = ball.y - point.y;
  let dist = length(dx, dy);
  const minDist = ball.r + FLIPPER_RADIUS;
  if (dist >= minDist) return false;
  if (dist < 0.000001) {
    const normal = normalize(-(tip.y - definition.pivot.y), tip.x - definition.pivot.x);
    dx = normal.x;
    dy = normal.y;
    dist = 1;
  }
  const nx = dx / dist, ny = dy / dist;
  ball.x += nx * (minDist - dist + 0.01);
  ball.y += ny * (minDist - dist + 0.01);

  const rx = point.x - definition.pivot.x;
  const ry = point.y - definition.pivot.y;
  // Tangential contact velocity is the only source of powered kick.
  const surfaceVx = -flipper.omega * ry;
  const surfaceVy = flipper.omega * rx;
  const relativeNormal = (ball.vx - surfaceVx) * nx + (ball.vy - surfaceVy) * ny;
  if (relativeNormal < 0) {
    const impulse = -(1 + 0.67) * relativeNormal;
    ball.vx += nx * impulse;
    ball.vy += ny * impulse;
    // A little friction helps cradles and makes transfers more controllable.
    const tx = -ny, ty = nx;
    const tangentialSlip = (ball.vx - surfaceVx) * tx + (ball.vy - surfaceVy) * ty;
    const friction = clamp(-tangentialSlip * 0.08, -90, 90);
    ball.vx += tx * friction;
    ball.vy += ty * friction;
  }
  limitBallSpeed(ball);
  return true;
}

function ballInsideRect(ball, rect) {
  return (
    ball.x >= rect.x &&
    ball.x <= rect.x + rect.w &&
    ball.y >= rect.y &&
    ball.y <= rect.y + rect.h
  );
}

function sensorEvent(ball, sensor, now) {
  if (!ballInsideRect(ball, sensor)) return false;
  if (sensor.direction === "up" && ball.vy > -(sensor.minSpeed || 0)) return false;
  const nextAllowed = ball.sensorCooldowns[sensor.id] || 0;
  if (now < nextAllowed) return false;
  ball.sensorCooldowns[sensor.id] = now + 0.85;
  return true;
}

function cooldownHit(ball, key, now, seconds = 0.14) {
  const nextAllowed = ball.collisionCooldowns[key] || 0;
  if (now < nextAllowed) return false;
  ball.collisionCooldowns[key] = now + seconds;
  return true;
}

function updateEffects(world, dt) {
  for (let i = world.effects.length - 1; i >= 0; i -= 1) {
    const effect = world.effects[i];
    effect.life -= dt;
    if (effect.life <= 0) {
      world.effects.splice(i, 1);
      continue;
    }
    effect.x += effect.vx * dt;
    effect.y += effect.vy * dt;
    effect.vx *= Math.pow(0.92, dt * 60);
    effect.vy *= Math.pow(0.92, dt * 60);
  }
  world.flash = Math.max(0, world.flash - dt * 2.5);
  world.shake = Math.max(0, world.shake - dt * 16);
}

function physicsSubstep(world, dt, controls, events) {
  world.time += dt;
  updateFlipper(world.leftFlipper, LEFT_FLIPPER, !!controls.left, dt);
  updateFlipper(world.rightFlipper, RIGHT_FLIPPER, !!controls.right, dt);

  if (controls.plunger) {
    world.plungerHeld = true;
    world.plungerPower = clamp(world.plungerPower + dt * 0.68, 0, 1);
  } else if (world.plungerHeld) {
    world.plungerHeld = false;
    const launchBall = world.balls.find(
      (ball) => ball.shooterReady && ball.x > 620 && ball.y > 820
    );
    if (launchBall) {
      // A light pull should still make a clean, controlled attempt rather than
      // rattling around the shooter tube. Full power reaches the upper arch.
      const power = Math.max(0.12, world.plungerPower);
      launchBall.shooterReady = false;
      launchBall.pendingSoftReturn = true;
      launchBall.vx = -8 - power * 18;
      launchBall.vy = -(620 + power * 930);
      events.push({ type: "launch", power, ballId: launchBall.id });
      spawnEffect(world, launchBall.x, launchBall.y + 18, "#ffcf53", 12, 100);
    }
    world.plungerPower = 0;
  }

  for (let index = world.balls.length - 1; index >= 0; index -= 1) {
    const ball = world.balls[index];

    // Shooter-lane cradle: before launch (or after a failed/soft launch rolls
    // all the way back), hold the ball cleanly against the plunger instead of
    // letting it chatter against the bottom stop.
    if (ball.shooterReady) {
      ball.x += (649 - ball.x) * Math.min(1, dt * 18);
      ball.y += (982 - ball.y) * Math.min(1, dt * 18);
      ball.vx *= Math.pow(0.08, dt);
      ball.vy *= Math.pow(0.08, dt);
      if (Math.abs(ball.x - 649) < 0.35) ball.x = 649;
      if (Math.abs(ball.y - 982) < 0.35) ball.y = 982;
    } else {
      ball.vy += GRAVITY * dt;
    }
    ball.vx *= Math.pow(0.999, dt * 60);
    ball.vy *= Math.pow(0.9994, dt * 60);
    ball.x += ball.vx * dt;
    ball.y += ball.vy * dt;

    // Outer cabinet edges are ONE-SIDED, unlike an ordinary rail: a ball
    // cannot exist on the back of the cabinet wall. Without this, a rare
    // glancing hit at the upper-left arch can place it outside the boundary,
    // where the lower-edge safety clamp and arch trap it at (44,132).
    // Evaluate only normal playfield balls: overhead guided paths are above
    // the cabinet and deliberately cross decorative physical rails.
    if (!ball.route && !ball.shooterReady) {
      let leftBoundary = 48;
      if (ball.y < 165 && ball.y >= 105)
        leftBoundary = 48 + (165 - ball.y) * (29 / 60);
      else if (ball.y < 105)
        leftBoundary = 77 + (105 - Math.max(58, ball.y)) * (68 / 47);
      if (ball.y <= 846 && ball.x < leftBoundary + ball.r + 4) {
        ball.x = leftBoundary + ball.r + 4;
        if (ball.vx < 0) ball.vx = -ball.vx * 0.7;
      }
      if (ball.y < 76 && ball.x > 144 && ball.x < 545) {
        ball.y = 76;
        if (ball.vy < 0) ball.vy *= -0.7;
      }
    }
    if (ball.x > 694 - ball.r) {
      ball.x = 694 - ball.r;
      if (ball.vx > 0) ball.vx *= -0.74;
    }
    if (ball.x < 30 + ball.r) {
      ball.x = 30 + ball.r;
      if (ball.vx < 0) ball.vx *= -0.74;
    }

    // Engage the visible guide at the shooter-lane mouth. Unlike the earlier
    // rollover this has continuous ball motion, with no coordinate jump.
    if (!ball.shooterReady && !ball.route && ball.x > 626 && ball.y <= SHOOTER_PATH.start[1] && ball.vy < -300) {
      ball.route = makeRoute(ball, SHOOTER_PATH, "shooter");
      // Start precisely where the ball entered, without ANY position jump.
      ball.vx = 0;
      ball.vy = 0;
    }

    if (ball.route) {
      const oldX = ball.x, oldY = ball.y;
      const route = ball.route;
      route.t = Math.min(1, route.t + dt / route.duration);
      const next = cubicPoint(route, route.t);
      ball.x = next.x;
      ball.y = next.y;
      ball.vx = (next.x - oldX) / dt;
      ball.vy = (next.y - oldY) / dt;
      // Rollovers traversed while riding a wireform count as lanes.
      for (const sensor of SENSORS) {
        if (sensor.type === "lane" && sensorEvent(ball, sensor, world.time))
          events.push({ type: sensor.type, index: sensor.index, id: sensor.id, ballId: ball.id });
      }
      if (route.t >= 1) {
        ball.route = null;
        const [vx,vy] = route.exitVelocity || [-175, 170 + route.entrySpeed * 0.04];
        ball.vx = vx;
        ball.vy = vy;
        if (route.type === "shooter") {
          ball.pendingSoftReturn = false;
          events.push({ type: "shooter-exit", ballId: ball.id });
        }
      }
      ball.lastX = ball.x;
      ball.lastY = ball.y;
      ball.trail.push({ x: ball.x, y: ball.y });
      if (ball.trail.length > 9) ball.trail.shift();
      continue;
    }

    STATIC_SEGMENTS.forEach((segment) => collideBallSegment(ball, segment));

    SLINGSHOTS.forEach((sling) => {
      const hit = collideBallSegment(ball, { a: sling.a, b: sling.b, radius: 8 }, 0.85);
      if (hit && cooldownHit(ball, `sling-${sling.id}`, world.time, 0.25)) {
        const near = closestPointOnSegment(ball.x, ball.y, ...sling.a, ...sling.b);
        const direction = normalize(ball.x - near.x, ball.y - near.y);
        // A real slingshot fires once as its switch closes, not every step.
        const outward = ball.vx * direction.x + ball.vy * direction.y;
        if (outward > 35) {
          // Each bounce off rubber loses some energy; this also prevents the
          // perfectly periodic left/right slingshot loop of frictionless 2D.
          ball.slingChain = world.time - ball.lastSlingTime < 2.4 ? ball.slingChain + 1 : 0;
          ball.lastSlingTime = world.time;
          const targetSpeed = Math.max(205, sling.boost - ball.slingChain * 32);
          const gain = Math.max(0, targetSpeed - outward);
          ball.vx += direction.x * gain;
          ball.vy += direction.y * gain;
          events.push({ type: "sling", id: sling.id, ballId: ball.id });
          world.shake = Math.max(world.shake, 3.5);
          spawnEffect(world, ball.x, ball.y, "#ff4d8f", 12, 130);
        }
      }
    });

    BUMPERS.forEach((bumper) => {
      const hit = collideBallCircle(ball, bumper, 0.89);
      if (hit && hit.incomingSpeed > 25 && cooldownHit(ball, `bumper-${bumper.id}`, world.time, 0.25)) {
        poweredImpact(ball, hit, bumper.boost);
        ball.slingChain = 0;
        events.push({ type: "bumper", id: bumper.id, ballId: ball.id });
        world.shake = Math.max(world.shake, 4.2);
        spawnEffect(world, bumper.x, bumper.y, "#6ef5ff", 16, 160);
      }
    });

    POSTS.forEach((post, postIndex) => {
      const hit = collideBallCircle(ball, post, post.id === "center-save" ? 0.88 : 0.84);
      if (hit && hit.incomingSpeed > 18 && cooldownHit(ball, `post-${postIndex}`, world.time, 0.15)) {
        if (post.id === "center-save" && ball.vy < 0) {
          // An actual rubber-mounted center post has some lateral bite. A
          // small, once-per-contact deflection prevents a perfectly straight
          // shot from bouncing vertically forever on top of this round post.
          ball.vx += (ball.vx < -12 ? -1 : 1) * 85;
          events.push({ type: "center-save", ballId: ball.id });
          spawnEffect(world, post.x, post.y, "#ffdb80", 9, 95);
        } else {
          events.push({ type: "post", id: postIndex, ballId: ball.id });
        }
      }
    });

    MISSION_TARGETS.forEach((target) => {
      const hit = collideBallRect(ball, target, 0.63);
      if (hit && hit.incomingSpeed > 20 && cooldownHit(ball, `mission-${target.id}`, world.time, 0.30)) {
        poweredImpact(ball, hit, 185);
        events.push({ type: "mission-target", id: target.id, ballId: ball.id });
        spawnEffect(world, target.x + target.w / 2, target.y + target.h / 2, "#ffb347", 10, 115);
      }
    });

    WARP_TARGETS.forEach((target) => {
      const hit = collideBallRect(ball, target, 0.63);
      if (hit && hit.incomingSpeed > 20 && cooldownHit(ball, `warp-${target.id}`, world.time, 0.30)) {
        poweredImpact(ball, hit, 185);
        events.push({ type: "warp-target", id: target.id, ballId: ball.id });
        spawnEffect(world, target.x + target.w / 2, target.y + target.h / 2, "#bd77ff", 10, 115);
      }
    });

    const reactorHit = collideBallCircle(ball, REACTOR, 0.85);
    if (reactorHit && reactorHit.incomingSpeed > 20 && cooldownHit(ball, "reactor", world.time, 0.3)) {
      poweredImpact(ball, reactorHit, 320);
      events.push({ type: "reactor", ballId: ball.id });
      world.flash = Math.max(world.flash, 0.55);
      world.shake = Math.max(world.shake, 4.5);
      spawnEffect(world, REACTOR.x, REACTOR.y, "#42d8ff", 20, 160);
    }

    const leftHit = collideBallFlipper(ball, world.leftFlipper, LEFT_FLIPPER);
    const rightHit = collideBallFlipper(ball, world.rightFlipper, RIGHT_FLIPPER);
    if ((leftHit || rightHit) && cooldownHit(ball, "flipper", world.time, 0.08)) {
      events.push({ type: "flipper", side: leftHit ? "left" : "right", ballId: ball.id });
    }

    SENSORS.forEach((sensor) => {
      if (ball.route || !sensorEvent(ball, sensor, world.time)) return;
      events.push({
        type: sensor.type,
        index: sensor.index,
        id: sensor.id,
        ballId: ball.id,
      });
      if (SHOT_PATHS[sensor.type]) {
        ball.route = makeRoute(ball, SHOT_PATHS[sensor.type], sensor.type);
        // Route starts at the actual sensor crossing point, not a fixed spot.
      }
    });

    // Outlanes now play as ordinary physical lanes. No invisible kickback
    // rectangles or lane-flow forces: those disguised blocked passageways.

    // Diagnostic last resort only: never teleport or randomize the ball.
    // A repeated "unstick" means the playfield needs geometry adjustment.
    const ballSpeed = length(ball.vx, ball.vy);
    const moved = length(ball.x - ball.lastX, ball.y - ball.lastY);
    if (!ball.shooterReady && ball.y < 990 && ballSpeed < 24 && moved < 0.15) {
      ball.stuckFor += dt;
    } else {
      ball.stuckFor = Math.max(0, ball.stuckFor - 2 * dt);
    }
    if (ball.stuckFor > 2.2) {
      ball.vx += ball.x < 360 ? 90 : -90;
      ball.vy += 130;
      ball.stuckFor = 0;
      events.push({ type: "unstick", x: ball.x, y: ball.y, ballId: ball.id });
    }
    ball.lastX = ball.x;
    ball.lastY = ball.y;

    // If a soft launch falls all the way back into the shooter lane, catch it
    // cleanly on the plunger so the player can try again.
    if (ball.pendingSoftReturn && !ball.shooterReady && ball.x > 625 && ball.y > 962 && ball.vy > 0) {
      ball.shooterReady = true;
      ball.pendingSoftReturn = false;
      ball.vx = 0;
      ball.vy = 0;
      ball.x = 649;
      ball.y = 982;
    }

    // A *live* ball entering the launch tube from below is an outlane drain,
    // never a magically recycled ball. A legitimate weak launch is exempt.
    // Shooter lane is otherwise excluded so a resting serve cannot drain.
    if ((!ball.shooterReady && !ball.pendingSoftReturn && ball.x > 625 && ball.y > 950) ||
        (ball.y > 1048 && ball.x < 606)) {
      events.push({ type: "drain", ballId: ball.id });
      world.balls.splice(index, 1);
      spawnEffect(world, ball.x, 1025, "#ff5a6c", 14, 120);
      continue;
    }

    // Recover a pathological stuck ball without changing gameplay state.
    if (ball.y < -80 || ball.x < -90 || ball.x > TABLE_WIDTH + 90) {
      ball.x = 360;
      ball.y = 170;
      ball.vx = 120;
      ball.vy = 110;
    }

    limitBallSpeed(ball);
    ball.trail.push({ x: ball.x, y: ball.y });
    if (ball.trail.length > 9) ball.trail.shift();
  }
}

export function stepPinballWorld(world, dt, controls = {}) {
  const events = [];
  const safeDt = clamp(dt || 0, 0, 0.05);
  world.accumulator += safeDt;

  let loops = 0;
  while (world.accumulator >= FIXED_STEP && loops < 12) {
    physicsSubstep(world, FIXED_STEP, controls, events);
    updateEffects(world, FIXED_STEP);
    world.accumulator -= FIXED_STEP;
    loops += 1;
  }

  return events;
}

function roundedRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function glowCircle(ctx, x, y, r, inner, outer, glow = 18) {
  ctx.save();
  ctx.shadowBlur = glow;
  ctx.shadowColor = outer;
  const gradient = ctx.createRadialGradient(x - r * 0.3, y - r * 0.35, r * 0.08, x, y, r);
  gradient.addColorStop(0, "#ffffff");
  gradient.addColorStop(0.16, inner);
  gradient.addColorStop(0.72, outer);
  gradient.addColorStop(1, "rgba(0,0,0,0.86)");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawMetalSegment(ctx, segment, width = 12) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = "rgba(0,0,0,.65)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 5;
  const gradient = ctx.createLinearGradient(segment.a[0], segment.a[1], segment.b[0], segment.b[1]);
  gradient.addColorStop(0, "#9da6b2");
  gradient.addColorStop(0.35, "#f3f7fb");
  gradient.addColorStop(0.52, "#68717e");
  gradient.addColorStop(1, "#232b35");
  ctx.strokeStyle = gradient;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(segment.a[0], segment.a[1]);
  ctx.lineTo(segment.b[0], segment.b[1]);
  ctx.stroke();
  ctx.restore();
}

function drawTarget(ctx, target, label, active, color) {
  ctx.save();
  ctx.shadowBlur = active ? 20 : 6;
  ctx.shadowColor = color;
  const gradient = ctx.createLinearGradient(target.x, target.y, target.x + target.w, target.y + target.h);
  gradient.addColorStop(0, active ? "#fdfcff" : "#d4d7dd");
  gradient.addColorStop(0.42, active ? color : "#7d8794");
  gradient.addColorStop(1, "#222833");
  roundedRect(ctx, target.x, target.y, target.w, target.h, 6);
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.strokeStyle = active ? color : "rgba(255,255,255,.25)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.translate(target.x + target.w / 2, target.y + target.h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = active ? "#fff" : "rgba(255,255,255,.68)";
  ctx.font = "800 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

function drawFlipper(ctx, definition, state, color) {
  const tip = flipperTip(definition, state.angle);
  ctx.save();
  ctx.lineCap = "round";
  ctx.shadowBlur = 16;
  ctx.shadowColor = color;
  ctx.strokeStyle = "rgba(0,0,0,.5)";
  ctx.lineWidth = FLIPPER_RADIUS * 2 + 7;
  ctx.beginPath();
  ctx.moveTo(definition.pivot.x, definition.pivot.y);
  ctx.lineTo(tip.x, tip.y);
  ctx.stroke();

  const gradient = ctx.createLinearGradient(
    definition.pivot.x,
    definition.pivot.y,
    tip.x,
    tip.y
  );
  gradient.addColorStop(0, "#f8f8f4");
  gradient.addColorStop(0.45, "#fff6de");
  gradient.addColorStop(1, color);
  ctx.strokeStyle = gradient;
  ctx.lineWidth = FLIPPER_RADIUS * 2;
  ctx.beginPath();
  ctx.moveTo(definition.pivot.x, definition.pivot.y);
  ctx.lineTo(tip.x, tip.y);
  ctx.stroke();
  ctx.strokeStyle = "rgba(255,255,255,.85)";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(definition.pivot.x + 2, definition.pivot.y - 5);
  ctx.lineTo(tip.x - 3, tip.y - 5);
  ctx.stroke();
  ctx.restore();
}

function drawBall(ctx, ball) {
  ball.trail.forEach((point, index) => {
    const alpha = (index + 1) / ball.trail.length * 0.13;
    ctx.fillStyle = `rgba(99,232,255,${alpha})`;
    ctx.beginPath();
    ctx.arc(point.x, point.y, ball.r * (0.35 + index / ball.trail.length * 0.5), 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.save();
  ctx.shadowBlur = 14;
  ctx.shadowColor = "rgba(255,255,255,.6)";
  const gradient = ctx.createRadialGradient(
    ball.x - ball.r * 0.35,
    ball.y - ball.r * 0.45,
    1,
    ball.x,
    ball.y,
    ball.r
  );
  gradient.addColorStop(0, "#ffffff");
  gradient.addColorStop(0.22, "#e9f0f7");
  gradient.addColorStop(0.58, "#8f9aa8");
  gradient.addColorStop(0.82, "#4a5260");
  gradient.addColorStop(1, "#171b21");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawLamp(ctx, x, y, active, color, label, radius = 10) {
  ctx.save();
  ctx.shadowBlur = active ? 18 : 0;
  ctx.shadowColor = color;
  ctx.fillStyle = active ? color : "rgba(35,44,58,.9)";
  ctx.strokeStyle = active ? "rgba(255,255,255,.8)" : "rgba(255,255,255,.18)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  if (label) {
    ctx.fillStyle = active ? "#fff" : "rgba(255,255,255,.5)";
    ctx.font = "900 10px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(label, x, y + radius + 15);
  }
  ctx.restore();
}

function drawPlayfieldArt(ctx, view = {}) {
  const laneLetters = ["H", "O", "B", "B", "L", "I"];
  const lanes = view.hoblliLanes || [];
  const missionTargets = view.missionTargets || [];
  const warpTargets = view.warpTargets || [];
  const t = view.animationTime || 0;

  // -----------------------------------------------------------------------
  // V3 visual direction: classic 1990s PC pinball.
  // The earlier board was intentionally sleek/minimal. This version uses a
  // busier illustrated playfield, chrome rails, molded plastics, incandescent
  // inserts and chunky mechanical-looking toys so it reads like an old cabinet
  // rather than a modern UI mockup.
  // -----------------------------------------------------------------------
  const bg = ctx.createLinearGradient(0, 0, 0, TABLE_HEIGHT);
  bg.addColorStop(0, "#14152c");
  bg.addColorStop(0.28, "#101b36");
  bg.addColorStop(0.64, "#15172f");
  bg.addColorStop(1, "#0c0b1d");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);

  // Printed playfield texture / subtle scanline grain.
  ctx.save();
  ctx.globalAlpha = 0.13;
  ctx.fillStyle = "#6a79a4";
  for (let y = 42; y < 1025; y += 7) ctx.fillRect(40, y, 640, 1);
  ctx.restore();

  seededStars.forEach((star) => {
    ctx.fillStyle = `rgba(190,220,255,${Math.min(.7, star.a * .55)})`;
    ctx.beginPath();
    ctx.arc(star.x, star.y, Math.max(.45, star.r * .75), 0, Math.PI * 2);
    ctx.fill();
  });

  // Retro printed planets / nebula artwork.
  const planet = (x, y, r, c1, c2, ring = false) => {
    ctx.save();
    const g = ctx.createRadialGradient(x - r * .35, y - r * .4, 2, x, y, r);
    g.addColorStop(0, c1);
    g.addColorStop(.58, c2);
    g.addColorStop(1, "#131323");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,.22)"; ctx.lineWidth = 2; ctx.stroke();
    if (ring) {
      ctx.strokeStyle = "rgba(213,168,255,.58)";
      ctx.lineWidth = 5;
      ctx.beginPath(); ctx.ellipse(x, y, r * 1.52, r * .38, -.32, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
  };
  planet(181, 270, 31, "#5dc5f4", "#214784");
  planet(518, 356, 28, "#ffbe64", "#69365a", true);
  planet(205, 520, 20, "#9878dc", "#38234f");

  // Large left molded-purple orbit plastic, echoing classic Space Cadet-era
  // tables without copying any art asset.
  ctx.save();
  ctx.shadowBlur = 12;
  ctx.shadowColor = "rgba(142,76,215,.45)";
  const leftPlastic = ctx.createLinearGradient(82, 248, 230, 430);
  leftPlastic.addColorStop(0, "rgba(176,111,232,.9)");
  leftPlastic.addColorStop(.55, "rgba(95,54,161,.88)");
  leftPlastic.addColorStop(1, "rgba(41,28,91,.94)");
  ctx.fillStyle = leftPlastic;
  ctx.strokeStyle = "rgba(218,186,255,.55)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(70, 240);
  ctx.bezierCurveTo(92, 204, 128, 205, 154, 231);
  ctx.bezierCurveTo(198, 275, 208, 327, 166, 364);
  ctx.bezierCurveTo(143, 384, 127, 408, 119, 445);
  ctx.lineTo(87, 430);
  ctx.bezierCurveTo(95, 377, 108, 343, 132, 318);
  ctx.bezierCurveTo(151, 298, 143, 272, 121, 257);
  ctx.bezierCurveTo(103, 245, 88, 248, 70, 270);
  ctx.closePath();
  ctx.fill(); ctx.stroke();
  ctx.restore();

  // Right upper red/blue orbit plastic.
  ctx.save();
  const rp = ctx.createLinearGradient(520, 205, 620, 390);
  rp.addColorStop(0, "rgba(68,126,185,.9)");
  rp.addColorStop(.5, "rgba(46,65,124,.95)");
  rp.addColorStop(1, "rgba(92,37,95,.95)");
  ctx.fillStyle = rp; ctx.strokeStyle = "rgba(170,217,255,.5)"; ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(515, 187); ctx.lineTo(546, 205); ctx.lineTo(550, 338);
  ctx.lineTo(533, 392); ctx.lineTo(512, 366); ctx.lineTo(523, 319); ctx.lineTo(521, 227); ctx.closePath();
  ctx.fill(); ctx.stroke();
  ctx.restore();

  // Top rollover / command panel.
  ctx.save();
  roundedRect(ctx, 170, 76, 375, 84, 10);
  ctx.fillStyle = "rgba(6,10,22,.72)";
  ctx.fill();
  ctx.strokeStyle = "rgba(137,163,202,.4)"; ctx.lineWidth = 2; ctx.stroke();
  ctx.fillStyle = "#b9c7e4"; ctx.font = "900 11px 'Courier New', monospace"; ctx.textAlign = "center";
  ctx.fillText("ORBITAL COMMAND", 357, 94);
  laneLetters.forEach((letter, index) => {
    const sensor = SENSORS[index];
    const x = sensor.x + sensor.w / 2;
    const y = 122;
    drawLamp(ctx, x, y, !!lanes[index], index % 2 ? "#f1d94e" : "#5de3ff", letter, 10);
  });
  ctx.restore();

  // Classic incandescent triangle inserts dotted through the middle.
  const triangleLamp = (x, y, active = false, color = "#e7d64a", angle = 0) => {
    ctx.save(); ctx.translate(x, y); ctx.rotate(angle);
    ctx.shadowBlur = active ? 16 : 6; ctx.shadowColor = color;
    ctx.fillStyle = active ? color : "rgba(74,76,64,.82)";
    ctx.strokeStyle = active ? "#fff5ae" : "rgba(255,255,255,.18)";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0,-10); ctx.lineTo(9,8); ctx.lineTo(-9,8); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.restore();
  };
  [[176,322],[200,375],[521,298],[505,405],[251,394],[466,455],[285,505],[435,520]].forEach((p,i)=>
    triangleLamp(p[0],p[1], ((Math.floor(t*4)+i)%5===0), i%2 ? "#ff7d45" : "#d8e84b", (i%3-1)*.22)
  );

  // Mission target bank on left.
  ctx.fillStyle = "#ffc14d";
  ctx.font = "900 13px 'Arial Narrow', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("MISSION", 121, 383);
  MISSION_TARGETS.forEach((target, index) =>
    drawTarget(ctx, target, String(index + 1), !!missionTargets[index], "#ffac39")
  );

  // Warp target bank on right.
  ctx.fillStyle = "#c57aff";
  ctx.fillText("WARP", 522, 383);
  WARP_TARGETS.forEach((target, index) =>
    drawTarget(ctx, target, String(index + 1), !!warpTargets[index], "#b85cff")
  );

  // Three classic pop bumpers: cream/red/blue instead of modern neon spheres.
  BUMPERS.forEach((bumper, index) => {
    ctx.save();
    ctx.shadowBlur = 14; ctx.shadowColor = index === 1 ? "#ef5e76" : "#7bd5ff";
    ctx.fillStyle = "#e7e3d5";
    ctx.beginPath(); ctx.arc(bumper.x, bumper.y, bumper.r + 6, 0, Math.PI*2); ctx.fill();
    ctx.strokeStyle = "#30384a"; ctx.lineWidth = 4; ctx.stroke();
    ctx.strokeStyle = index === 1 ? "#b52e4b" : "#276aa0"; ctx.lineWidth = 8;
    ctx.beginPath(); ctx.arc(bumper.x, bumper.y, bumper.r - 6, 0, Math.PI*2); ctx.stroke();
    ctx.fillStyle = index === 1 ? "#f05c76" : "#6bc7ed";
    ctx.beginPath(); ctx.arc(bumper.x, bumper.y, 13, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = "#152036"; ctx.font = "900 11px system-ui,sans-serif"; ctx.textAlign="center"; ctx.textBaseline="middle";
    ctx.fillText(index === 1 ? "V" : "O", bumper.x, bumper.y);
    ctx.restore();
  });

  // Center vault ramp artwork / rails.
  ctx.save();
  ctx.fillStyle = "rgba(31,47,83,.72)";
  ctx.beginPath(); ctx.moveTo(300,430); ctx.lineTo(320,344); ctx.lineTo(360,310); ctx.lineTo(402,344); ctx.lineTo(420,430); ctx.lineTo(394,414); ctx.lineTo(383,356); ctx.lineTo(360,337); ctx.lineTo(338,356); ctx.lineTo(326,414); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = "rgba(163,209,230,.8)"; ctx.lineWidth = 5; ctx.stroke();
  ctx.fillStyle = "#66d7ef"; ctx.font = "900 12px 'Arial Narrow',system-ui,sans-serif"; ctx.textAlign="center"; ctx.fillText("VAULT RAMP",360,404);
  ctx.restore();

  // Reactor saucer / center feature: flatter and more mechanical than V2.
  ctx.save();
  ctx.shadowBlur = view.multiballReady ? 24 : 14;
  ctx.shadowColor = view.multiballReady ? "#c45cff" : "#30bfe6";
  ctx.fillStyle = "#172136";
  ctx.beginPath(); ctx.arc(REACTOR.x, REACTOR.y, REACTOR.r + 9, 0, Math.PI*2); ctx.fill();
  ctx.strokeStyle = "#667386"; ctx.lineWidth = 6; ctx.stroke();
  const rg = ctx.createRadialGradient(348,551,3,REACTOR.x,REACTOR.y,REACTOR.r-8);
  rg.addColorStop(0,"#bff8ff"); rg.addColorStop(.22,"#39bee0"); rg.addColorStop(.65,"#1e627c"); rg.addColorStop(1,"#111d2a");
  ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(REACTOR.x,REACTOR.y,REACTOR.r-8,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle = "rgba(112,223,246,.7)"; ctx.lineWidth=3; ctx.stroke();
  for (let i=0;i<12;i++) {
    const a=Math.PI*2*i/12;
    drawLamp(ctx, REACTOR.x+Math.cos(a)*73, REACTOR.y+Math.sin(a)*73,
      i%3===Math.floor(t*6)%3,
      view.multiballReady ? "#d074ff" : "#58deef", "", 5);
  }
  ctx.fillStyle="#e7faff"; ctx.font="900 13px system-ui,sans-serif"; ctx.textAlign="center";
  ctx.fillText(view.multiballReady ? "MULTIBALL" : view.missionReady ? "MISSION" : "REACTOR",360,570);
  ctx.restore();

  // Score inserts / labels near center feature.
  drawLamp(ctx, 253, 633, !!view.missionReady, "#f7c54d", "MISSION", 8);
  drawLamp(ctx, 467, 633, !!view.multiballReady, "#c176ff", "WARP", 8);
  drawLamp(ctx, 360, 684, !!view.multiball, "#f65dc2", "JACKPOT", 9);

  // Smaller, classic 90s slingshot plastics. V3's large blue/purple slabs
  // visually filled the side lanes and matched the same areas that could trap
  // the ball. V4 keeps the return lanes visibly OPEN all the way to the bats.
  ctx.save();
  const lp = ctx.createLinearGradient(162,748,246,842);
  lp.addColorStop(0,"rgba(123,67,190,.94)"); lp.addColorStop(.5,"rgba(55,121,197,.92)"); lp.addColorStop(1,"rgba(26,45,100,.96)");
  ctx.fillStyle=lp; ctx.strokeStyle="rgba(184,220,245,.72)"; ctx.lineWidth=3;
  ctx.beginPath(); ctx.moveTo(195,766); ctx.lineTo(244,840); ctx.lineTo(203,816); ctx.closePath(); ctx.fill(); ctx.stroke();
  const rp2=ctx.createLinearGradient(558,748,474,842);
  rp2.addColorStop(0,"rgba(123,67,190,.94)"); rp2.addColorStop(.5,"rgba(55,121,197,.92)"); rp2.addColorStop(1,"rgba(26,45,100,.96)");
  ctx.fillStyle=rp2;
  ctx.beginPath(); ctx.moveTo(525,766); ctx.lineTo(476,840); ctx.lineTo(517,816); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.restore();

  // Open return-lane arrows and outlane lamps.
  const returnArrow = (x,y,flip=false) => {
    ctx.save(); ctx.translate(x,y); ctx.rotate(Math.PI); if(flip) ctx.scale(-1,1);
    ctx.fillStyle="rgba(69,217,238,.9)"; ctx.shadowBlur=8; ctx.shadowColor="#52d9ef";
    ctx.beginPath(); ctx.moveTo(0,-13);ctx.lineTo(10,4);ctx.lineTo(4,3);ctx.lineTo(4,15);ctx.lineTo(-4,15);ctx.lineTo(-4,3);ctx.lineTo(-10,4);ctx.closePath();ctx.fill();ctx.restore();
  };
  // Inked inlane arrows point DOWN to the flippers; these are never colliders.
  returnArrow(226,863); returnArrow(494,863,true);
  drawLamp(ctx, 105, 810, false, "#ffc14c", "OUT", 7);
  drawLamp(ctx, 590, 810, false, "#ffc14c", "OUT", 7);

  // Vintage score/multiplier inserts along lower center.
  [2,3,4,5].forEach((m,i)=>{
    const x=300+i*40;
    const active=(view.multiplier||1)>=m;
    drawLamp(ctx,x,760,active,"#ffb43d",`${m}X`,8);
  });

  // Orbit labels.
  ctx.save(); ctx.fillStyle="rgba(221,226,242,.72)";ctx.font="900 10px 'Courier New',monospace";
  ctx.translate(79,315);ctx.rotate(-Math.PI/2);ctx.textAlign="center";ctx.fillText("COMET ORBIT",0,0);ctx.restore();
  ctx.save(); ctx.fillStyle="rgba(221,226,242,.72)";ctx.font="900 10px 'Courier New',monospace";
  ctx.translate(604,315);ctx.rotate(Math.PI/2);ctx.textAlign="center";ctx.fillText("NOVA ORBIT",0,0);ctx.restore();

  // Printed lower apron art: big purple 'fins' like classic software pinball.
  ctx.save();
  const fin = ctx.createLinearGradient(235,865,360,1005);
  fin.addColorStop(0,"rgba(70,57,150,.72)");fin.addColorStop(.5,"rgba(118,50,177,.78)");fin.addColorStop(1,"rgba(43,23,78,.85)");
  ctx.fillStyle=fin;
  ctx.beginPath();ctx.moveTo(270,850);ctx.lineTo(337,1008);ctx.lineTo(352,850);ctx.lineTo(318,932);ctx.closePath();ctx.fill();
  ctx.beginPath();ctx.moveTo(450,850);ctx.lineTo(383,1008);ctx.lineTo(368,850);ctx.lineTo(402,932);ctx.closePath();ctx.fill();
  ctx.restore();

  // Playfield branding, intentionally small like a real machine rather than a
  // giant title pasted into the middle of the board.
  ctx.save();
  ctx.fillStyle="#89e6f5";ctx.font="900 16px system-ui,sans-serif";ctx.textAlign="center";
  ctx.fillText("ORBITAL VAULT",360,186);
  ctx.fillStyle="#f3c34e";ctx.font="900 9px 'Courier New',monospace";ctx.fillText("HOBBLI PINBALL DIVISION",360,201);
  roundedRect(ctx,88,996,544,54,14);ctx.fillStyle="rgba(4,7,15,.65)";ctx.fill();
  ctx.fillStyle="#68e0ef";ctx.font="900 16px system-ui,sans-serif";ctx.textAlign="left";ctx.fillText("HOBBLI",110,1022);
  ctx.fillStyle="rgba(230,234,243,.65)";ctx.font="800 9px 'Courier New',monospace";ctx.fillText("ORBITAL VAULT // TABLE 01",110,1038);
  ctx.textAlign="right";ctx.fillText("LIGHT · LOCK · LAUNCH",608,1031);
  ctx.restore();
}

export function renderPinballWorld(ctx, world, view = {}) {
  if (!ctx) return;
  const canvas = ctx.canvas;
  ctx.save();
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const scaleX = canvas.width / TABLE_WIDTH;
  const scaleY = canvas.height / TABLE_HEIGHT;
  ctx.scale(scaleX, scaleY);

  const shake = world.shake > 0 ? world.shake : 0;
  if (shake) {
    ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);
  }

  drawPlayfieldArt(ctx, { ...view, animationTime: world.time });

  // Outer cabinet shadow / bevel.
  ctx.save();
  ctx.strokeStyle = "#04070d";
  ctx.lineWidth = 28;
  roundedRect(ctx, 15, 15, TABLE_WIDTH - 30, TABLE_HEIGHT - 30, 32);
  ctx.stroke();
  ctx.strokeStyle = "rgba(176,194,215,.62)";
  ctx.lineWidth = 5;
  roundedRect(ctx, 30, 30, TABLE_WIDTH - 60, TABLE_HEIGHT - 60, 25);
  ctx.stroke();
  ctx.restore();

  // Chrome top arch and orbit rails add a more physical cabinet feel.
  ctx.save();
  const chrome = ctx.createLinearGradient(80, 55, 610, 110);
  chrome.addColorStop(0, "#535b68"); chrome.addColorStop(.28, "#f5fbff"); chrome.addColorStop(.5, "#7d8795"); chrome.addColorStop(.72, "#eef5fb"); chrome.addColorStop(1, "#3b424d");
  ctx.strokeStyle = chrome; ctx.lineWidth = 9; ctx.lineCap = "round"; ctx.shadowBlur = 8; ctx.shadowColor = "rgba(0,0,0,.7)";
  ctx.beginPath(); ctx.moveTo(92, 108); ctx.quadraticCurveTo(150, 45, 210, 43); ctx.lineTo(526,43); ctx.quadraticCurveTo(565,48, 590,88); ctx.stroke();
  ctx.restore();

  // Rails and walls.
  STATIC_SEGMENTS.forEach((segment) => drawMetalSegment(ctx, segment, 11));

  // Shooter lane arrow and plunger.
  ctx.save();
  ctx.fillStyle = "rgba(255,207,83,.72)";
  ctx.font = "900 11px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("LAUNCH", 651, 795);
  ctx.strokeStyle = "rgba(255,207,83,.75)";
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(651, 810); ctx.lineTo(651, 862); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(642, 823); ctx.lineTo(651, 810); ctx.lineTo(660, 823); ctx.stroke();

  // Plunger is BELOW the ball/cradle, not drawn through its center.
  const plungerY = 1038 + world.plungerPower * 14;
  const plungerGradient = ctx.createLinearGradient(632, 950, 674, 1042);
  plungerGradient.addColorStop(0, "#eef3f8");
  plungerGradient.addColorStop(0.4, "#7e8794");
  plungerGradient.addColorStop(1, "#222a34");
  ctx.fillStyle = plungerGradient;
  // spring
  ctx.strokeStyle = "rgba(184,200,220,.72)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  for (let i = 0; i <= 8; i += 1) {
    const sy = 1019 + i * ((plungerY - 1027) / 8);
    const sx = 651 + (i % 2 === 0 ? -8 : 8);
    if (i === 0) ctx.moveTo(651, sy); else ctx.lineTo(sx, sy);
  }
  ctx.stroke();
  ctx.fillStyle = plungerGradient;
  roundedRect(ctx, 636, plungerY - 9, 30, 18, 9);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,.56)";
  ctx.stroke();
  ctx.restore();

  // The physical shooter wireform is visible, and matches the continuous
  // guided ball trajectory. It is not a teleport disguised as a gate.
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineWidth = 5;
  ctx.strokeStyle = "#96cfe6";
  ctx.shadowColor = "#4daacc";
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.moveTo(...SHOOTER_PATH.start);
  ctx.bezierCurveTo(...SHOOTER_PATH.c1, ...SHOOTER_PATH.c2, ...SHOOTER_PATH.end);
  ctx.stroke();
  ctx.restore();

  // Raised wireforms: double SOLID rails and explicit intake/drop markers.
  // Their elevated sections are not wall colliders; a ball on a track is
  // controlled by the same Bezier definition, using its actual intake point.
  // No dashed lines that look like dead-end physical rails.
  Object.entries(SHOT_PATHS).forEach(([name, path]) => {
    const sensor = SENSORS.find((entry) => entry.type === name);
    const start = [sensor.x + sensor.w * 0.5, sensor.y + sensor.h * 0.5];
    const trackColor = name === "ramp" ? "#ffbe61" : "#69ddfb";
    const trace = (offsetY) => {
      ctx.beginPath(); ctx.moveTo(start[0], start[1] + offsetY);
      ctx.bezierCurveTo(path.c1[0], path.c1[1] + offsetY, path.c2[0], path.c2[1] + offsetY, path.end[0], path.end[1] + offsetY);
    };
    ctx.save();
    ctx.lineCap = "round";
    // A dark, unmistakably raised bed separates the overhead track from playfield art.
    trace(0); ctx.lineWidth = 24; ctx.strokeStyle = "rgba(6,15,28,.96)"; ctx.stroke();
    for (const offset of [-9, 9]) {
      trace(offset); ctx.lineWidth = 3.5; ctx.strokeStyle = "#bed9e7"; ctx.stroke();
    }
    trace(0); ctx.lineWidth = 5; ctx.strokeStyle = trackColor; ctx.globalAlpha = .48; ctx.stroke();
    // Ball-sized openings at both ends (painted on top so there is no mystery
    // about where the shot enters or where it is returned to the field).
    for (const [x,y,caption] of [[start[0],start[1],"IN"],[path.end[0],path.end[1],"OUT"]]) {
      ctx.fillStyle = "#101b2d"; ctx.strokeStyle = trackColor;
      ctx.lineWidth = 3; ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.arc(x,y,16,0,Math.PI*2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#f2faff"; ctx.font = "bold 9px system-ui,sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(caption,x,y);
    }
    ctx.restore();
  });

  // Bumper artwork is already painted in drawPlayfieldArt. Draw only the
  // physical post hardware here so the visual style stays classic rather than
  // layering the old neon bumper renderer on top.
  POSTS.forEach((post, index) => {
    ctx.save();
    ctx.shadowBlur = post.id === "center-save" ? 16 : 5;
    ctx.shadowColor = post.id === "center-save" ? "#ffc253" : "rgba(0,0,0,.7)";
    const g = ctx.createRadialGradient(post.x - 4, post.y - 5, 2, post.x, post.y, post.r);
    g.addColorStop(0, "#ffffff");
    g.addColorStop(.28, "#d8dde4");
    g.addColorStop(.58, "#7d8792");
    g.addColorStop(1, "#252b34");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(post.x, post.y, post.r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = post.id === "center-save" ? "#ffc960" : index < 2 ? "#d18d35" : "rgba(255,255,255,.35)";
    ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = "#343941";
    ctx.beginPath(); ctx.arc(post.x, post.y, 3, 0, Math.PI * 2); ctx.fill();
    if (post.id === "center-save") {
      ctx.strokeStyle = "rgba(255,199,90,.48)";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(post.x, post.y, 18, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = "#ffe1a0";
      ctx.font = "900 9px system-ui,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("SAVE", post.x, post.y - 25);
    }
    ctx.restore();
  });

  // Slingshot rubber lines.
  SLINGSHOTS.forEach((sling) => {
    ctx.save();
    ctx.strokeStyle = "#ff577f";
    ctx.shadowBlur = 14;
    ctx.shadowColor = "#ff3879";
    ctx.lineWidth = 8;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(sling.a[0], sling.a[1]);
    ctx.lineTo(sling.b[0], sling.b[1]);
    ctx.stroke();
    ctx.restore();
  });

  drawFlipper(ctx, LEFT_FLIPPER, world.leftFlipper, "#ff4e73");
  drawFlipper(ctx, RIGHT_FLIPPER, world.rightFlipper, "#ff4e73");

  world.effects.forEach((effect) => {
    const alpha = clamp(effect.life / effect.maxLife, 0, 1);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowBlur = 10;
    ctx.shadowColor = effect.color;
    ctx.fillStyle = effect.color;
    ctx.beginPath();
    ctx.arc(effect.x, effect.y, effect.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });

  world.balls.forEach((ball) => drawBall(ctx, ball));

  if (world.flash > 0) {
    ctx.save();
    ctx.globalAlpha = world.flash * 0.14;
    ctx.fillStyle = "#9feaff";
    ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    ctx.restore();
  }

  ctx.restore();
}

export const PINBALL_GEOMETRY = {
  staticSegments: STATIC_SEGMENTS,
  slingshots: SLINGSHOTS,
  posts: POSTS,
  leftFlipper: LEFT_FLIPPER,
  rightFlipper: RIGHT_FLIPPER,
  shooterPath: SHOOTER_PATH,
  shotPaths: SHOT_PATHS,
  sensors: SENSORS,
  bumpers: BUMPERS,
  missionTargets: MISSION_TARGETS,
  warpTargets: WARP_TARGETS,
  reactor: REACTOR,
};
